export type ContactSourceSchemaErrorCode =
  | 'unnamed_columns_with_data'
  | 'duplicate_headers'
  | 'no_named_columns';

export class ContactSourceSchemaPreviewError extends Error {
  readonly code: ContactSourceSchemaErrorCode;
  readonly details: string[];

  constructor(code: ContactSourceSchemaErrorCode, details: string[] = []) {
    super(code);
    this.name = 'ContactSourceSchemaPreviewError';
    this.code = code;
    this.details = details;
  }
}

interface ContactSourceColumnPlan {
  namedIndexes: number[];
  namedHeaders: string[];
  unnamedIndexes: number[];
  sourceColumnCount: number;
}

export interface StructuredContactSourceRows {
  headers: string[];
  rows: Record<string, string>[];
  ignoredColumnIndexes: number[];
}

export function normalizeContactSourceCell(value: unknown): string {
  return String(value ?? '').trim().replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '');
}

export function generateContactSourceColumnName(index: number): string {
  let name = '';
  let current = index;
  while (current >= 0) {
    name = String.fromCharCode(97 + (current % 26)) + name;
    current = Math.floor(current / 26) - 1;
  }
  return `column_${name}`;
}

export function spreadsheetColumnLabel(index: number): string {
  if (index < 0) {
    throw new RangeError('Column index must be zero or positive.');
  }

  let label = '';
  let current = index + 1;
  while (current > 0) {
    const remainder = (current - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    current = Math.floor((current - 1) / 26);
  }
  return label;
}

export function formatSpreadsheetColumnIndexes(indexes: readonly number[]): string {
  const sortedIndexes = Array.from(new Set(indexes)).sort((left, right) => left - right);
  const ranges: string[] = [];

  for (let position = 0; position < sortedIndexes.length; position += 1) {
    const rangeStart = sortedIndexes[position];
    let rangeEnd = rangeStart;

    while (
      position + 1 < sortedIndexes.length
      && sortedIndexes[position + 1] === rangeEnd + 1
    ) {
      position += 1;
      rangeEnd = sortedIndexes[position];
    }

    const startLabel = spreadsheetColumnLabel(rangeStart);
    const endLabel = spreadsheetColumnLabel(rangeEnd);
    ranges.push(startLabel === endLabel ? startLabel : `${startLabel}-${endLabel}`);
  }

  return ranges.join(', ');
}

function buildContactSourceColumnPlan(rawHeaders: readonly string[]): ContactSourceColumnPlan {
  const normalizedHeaders = rawHeaders.map(normalizeContactSourceCell);
  const seenHeaders = new Set<string>();
  const duplicateHeaders: string[] = [];
  const namedIndexes: number[] = [];
  const namedHeaders: string[] = [];
  const unnamedIndexes: number[] = [];

  normalizedHeaders.forEach((header, index) => {
    if (!header) {
      unnamedIndexes.push(index);
      return;
    }

    if (seenHeaders.has(header) && !duplicateHeaders.includes(header)) {
      duplicateHeaders.push(header);
    }
    seenHeaders.add(header);
    namedIndexes.push(index);
    namedHeaders.push(header);
  });

  if (duplicateHeaders.length > 0) {
    throw new ContactSourceSchemaPreviewError('duplicate_headers', duplicateHeaders);
  }
  if (namedIndexes.length === 0) {
    throw new ContactSourceSchemaPreviewError('no_named_columns');
  }

  return {
    namedIndexes,
    namedHeaders,
    unnamedIndexes,
    sourceColumnCount: normalizedHeaders.length,
  };
}

function normalizeRowToColumnCount(row: readonly string[], columnCount: number): string[] {
  return Array.from(
    { length: columnCount },
    (_, index) => normalizeContactSourceCell(row[index] ?? ''),
  );
}

function buildRowsFromNamedColumns(
  rawRows: readonly string[][],
  headers: readonly string[],
  sourceIndexes: readonly number[],
): Record<string, string>[] {
  return rawRows.map((rawRow) => {
    const row: Record<string, string> = {};
    headers.forEach((header, visibleIndex) => {
      const sourceIndex = sourceIndexes[visibleIndex];
      row[header] = normalizeContactSourceCell(rawRow[sourceIndex] ?? '');
    });
    return row;
  });
}

export function structureContactSourceRows(
  rawRows: string[][],
  useFirstRowAsHeader: boolean,
  customNames?: Record<number, string>,
): StructuredContactSourceRows {
  if (rawRows.length === 0) {
    throw new Error('No data found in file');
  }

  const columnCount = rawRows.reduce(
    (currentMax, row) => Math.max(currentMax, row.length),
    0,
  );

  if (!useFirstRowAsHeader) {
    const generatedHeaders = Array.from(
      { length: columnCount },
      (_, index) => generateContactSourceColumnName(index),
    );
    const headers = generatedHeaders.map((header, index) => {
      const customName = normalizeContactSourceCell(customNames?.[index] ?? '');
      return customName || header;
    });
    const sourceIndexes = headers.map((_, index) => index);
    const rows = buildRowsFromNamedColumns(rawRows, headers, sourceIndexes);
    return { headers, rows, ignoredColumnIndexes: [] };
  }

  const headerRow = normalizeRowToColumnCount(rawRows[0], columnCount);
  const plan = buildContactSourceColumnPlan(headerRow);
  const dataRows = rawRows.slice(1);
  if (dataRows.length === 0) {
    throw new Error('No data rows found in file');
  }

  for (const row of dataRows) {
    const normalizedRow = normalizeRowToColumnCount(row, columnCount);
    const anonymousDataIndexes = plan.unnamedIndexes.filter(
      (index) => normalizeContactSourceCell(normalizedRow[index]),
    );
    if (anonymousDataIndexes.length > 0) {
      throw new ContactSourceSchemaPreviewError(
        'unnamed_columns_with_data',
        anonymousDataIndexes.map(spreadsheetColumnLabel),
      );
    }
  }

  const headers = plan.namedHeaders.map((header, index) => {
    const customName = normalizeContactSourceCell(customNames?.[index] ?? '');
    return customName || header;
  });
  const rows = buildRowsFromNamedColumns(dataRows, headers, plan.namedIndexes);

  return {
    headers,
    rows,
    ignoredColumnIndexes: plan.unnamedIndexes,
  };
}
